/**
 * [PURPOSE]: Convert a single EffectComposer passiveBonus row to the effectRegistry
 *   effect shape. Used by ConditionsTracker.toggleCondition to register
 *   passiveEffects authored in CustomConditionCreator.
 *
 * [SUB-PLAN 3B — 2026-05-30]: Closes the passiveEffects registration gap.
 *   CustomConditionCreator.handleSave stores EffectComposer rows in
 *   condition.passiveEffects (array). ConditionsTracker previously only
 *   registered condition.numericEffects. This bridge converts each passiveBonus
 *   row to a registry-compatible effect shape so effectCalculator reads
 *   the bonus automatically (e.g., AC penalty, speed reduction).
 *
 * [WHY A SEPARATE FILE]:
 *   Rule 22 — extract → import → assert. Both the production registration
 *   site (ConditionsTracker) and the Rule 31f bridge test import from here,
 *   so the test drives the SAME conversion the production code runs. No inline
 *   replica in the test.
 *
 * [CONVERSION]:
 *   EffectComposer row shape (stored in condition.passiveEffects):
 *     { id: string, kind: 'passiveBonus', attribute: TOGGLE_ATTRIBUTES key,
 *       value: number | string, operation?: 'add' | 'set' | 'multiply',
 *       condition: null | {when:flag} | {category,op,operands[]} }
 *
 *   effectRegistry effect shape:
 *     { type: string, operation: 'add'|'set'|'multiply', value: number, condition?: ... }
 *
 *   Mapping (mirrors CustomFeatCreator.handleSave lines 630-643 for feats):
 *     eff.attribute → type
 *     eff.operation → operation  (defaults to 'add' when absent — backward compat
 *                                 with rows authored before R1-rank4 2026-06-13)
 *     Number(eff.value) → value
 *     eff.condition → condition  (omitted when null or the string 'always')
 *
 * [PRECEDENT]: CustomFeatCreator.handleSave (src/components/CustomFeatCreator.js:630-643)
 *   applies the same field mapping when building feat.effects from feat.passiveEffects.
 *
 * [R1-rank4 — 2026-06-13]: Added `operation` field support. Rows now carry
 *   operation:'add'|'set'|'multiply'; rows without the field default to 'add'
 *   (backward compat). The CustomFeatCreator passive-effect row now renders
 *   an operation <select> with these three choices — matching what the
 *   effectRegistry getEffectiveValue switch actually handles for numeric stat mods.
 *
 * [RULE 22]: Shared module. Do NOT replicate this logic inline in tests or in
 *   ConditionsTracker. Import it.
 *
 * [INTEGRATION]:
 *   - src/components/ConditionsTracker.js — register call on toggleCondition apply
 *   - src/__tests__/3B-custom-condition-creator.test.js Group J — Rule 31f bridge test
 */

/**
 * Convert one EffectComposer passiveBonus row to an effectRegistry effect object.
 *
 * Returns null for rows that are not actionable (missing attribute, or value
 * that evaluates to 0 and is explicitly empty — skip rows the author left blank).
 * Rows with value=0 that were intentionally set are kept (0 is a valid no-op;
 * the author chose to include it). Only truly blank / missing attribute rows
 * are skipped.
 *
 * @param {Object} row - EffectComposer row: { id, kind, attribute, value, operation?, condition }
 * @returns {{ type: string, operation: 'add'|'set'|'multiply', value: number, condition?: * } | null}
 */
export function passiveBonusRowToEffect(row) {
  if (!row || !row.attribute) return null;

  // Numeric coercion — mirrors CustomFeatCreator.handleSave line 635.
  // If value is a formula token (string like 'proficiencyBonus'), keep as-is
  // so effectRegistry can handle it; otherwise coerce to Number.
  const rawValue = row.value;
  const numericValue = (typeof rawValue === 'string' && isNaN(Number(rawValue)))
    ? rawValue        // formula token — keep as string
    : Number(rawValue) || 0;

  // [R1-rank4 — 2026-06-13]: Honor per-row operation when present. Default 'add'
  // for backward compat with rows authored before the operation field existed.
  // Valid operations: 'add' | 'set' | 'multiply' — these are the only ones
  // effectRegistry.getEffectiveValue's switch handles for numeric stat mods.
  const VALID_OPERATIONS = new Set(['add', 'set', 'multiply']);
  const operation = (row.operation && VALID_OPERATIONS.has(row.operation))
    ? row.operation
    : 'add';

  const effect = {
    type: row.attribute,
    operation,
    value: numericValue,
  };

  // Attach condition only when present and not the trivial 'always' string.
  // null means "always" in ConditionKindSlot semantics.
  if (row.condition != null && row.condition !== 'always') {
    effect.condition = row.condition;
  }

  return effect;
}

/**
 * Convert an array of EffectComposer passiveBonus rows to effectRegistry effects.
 * Filters out null results (blank / invalid rows).
 *
 * @param {Array} rows - Array of EffectComposer rows from condition.passiveEffects
 * @returns {Array} Array of effectRegistry effect objects (may be empty)
 */
export function passiveBonusRowsToEffects(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(passiveBonusRowToEffect).filter(Boolean);
}

/**
 * [SUB-PLAN 3A-followup-effectcomposer-resistance-kind — 2026-05-30]
 *
 * Convert one EffectComposer resistance row to an effectRegistry effect object.
 *
 * EffectComposer resistance row shape:
 *   { id: string, kind: 'resistance', value: string (damageResistanceType id),
 *     condition: null | {when:flag} | {category,op,operands[]} }
 *
 * effectRegistry effect shape emitted:
 *   { type: 'resistance', operation: 'grant', value: string (damage type) }
 *   (+ condition when present and non-trivial)
 *
 * Maps to getDamageResistances() reader path:
 *   effectRegistry.getEffectsForStat('resistance', playerStats, {})
 *     .forEach(effect => { if (effect.operation === 'grant') resistances.add(effect.value); })
 *
 * Returns null when value is missing/empty (author hasn't picked a damage type yet).
 *
 * [RULE 22]: Shared module — import this in ConditionsTracker / CustomFeatCreator
 *   and in the Rule 31f bridge test. No inline replicas.
 *
 * @param {Object} row - EffectComposer row: { id, kind:'resistance', value, condition }
 * @returns {{ type: 'resistance', operation: 'grant', value: string, condition?: * } | null}
 */
export function resistanceRowToEffect(row) {
  if (!row || !row.value) return null;

  const effect = {
    type: 'resistance',
    operation: 'grant',
    value: row.value,
  };

  // Attach condition only when present and not the trivial 'always' string.
  // null means "always" in ConditionKindSlot semantics.
  if (row.condition != null && row.condition !== 'always') {
    effect.condition = row.condition;
  }

  return effect;
}

/**
 * [PB-1 — 2026-06-13]: Vulnerability authoring — mirror of resistanceRowToEffect.
 *
 * Convert one EffectComposer vulnerability row to an effectRegistry effect object.
 *
 * EffectComposer vulnerability row shape:
 *   { id: string, kind: 'vulnerability', value: string (damageResistanceType id),
 *     condition: null | {when:flag} | {category,op,operands[]} }
 *
 * effectRegistry effect shape emitted:
 *   { type: 'vulnerability', operation: 'grant', value: string (damage type) }
 *   (+ condition when present and non-trivial)
 *
 * Maps to getDamageVulnerabilities() reader path:
 *   effectRegistry.getEffectsForStat('vulnerability', playerStats, {})
 *     .forEach(effect => { if (effect.operation === 'grant') vulnerabilities.add(effect.value); })
 *
 * Reuses damageResistanceType kind (same 14 scopes incl nonmagical-composite) —
 * Rule 30c: no inline <select>, ReferencePicker(kind='damageResistanceType').
 *
 * Returns null when value is missing/empty (author hasn't picked a damage type yet).
 *
 * [RULE 22]: Shared module — import in ConditionsTracker and test. No inline replicas.
 *
 * @param {Object} row - EffectComposer row: { id, kind:'vulnerability', value, condition }
 * @returns {{ type: 'vulnerability', operation: 'grant', value: string, condition?: * } | null}
 */
export function vulnerabilityRowToEffect(row) {
  if (!row || !row.value) return null;

  const effect = {
    type: 'vulnerability',
    operation: 'grant',
    value: row.value,
  };

  // Attach condition only when present and not the trivial 'always' string.
  // null means "always" in ConditionKindSlot semantics.
  if (row.condition != null && row.condition !== 'always') {
    effect.condition = row.condition;
  }

  return effect;
}

/**
 * [SUB-PLAN 3A-followup-effectcomposer-resistance-kind — 2026-05-30]
 *
 * Unified dispatcher: converts any single EffectComposer row to an effectRegistry
 * effect object regardless of its `kind`. Dispatches to the appropriate per-kind
 * converter. New kinds are added here as a single switch branch — no callers need
 * to be updated.
 *
 * Supported kinds:
 *   'passiveBonus'  → passiveBonusRowToEffect(row)
 *   'resistance'    → resistanceRowToEffect(row)
 *   'vulnerability' → vulnerabilityRowToEffect(row)  [PB-1 — 2026-06-13]
 *
 * Returns null for unknown/unsupported kinds or when the per-kind converter returns
 * null (invalid / incomplete row).
 *
 * [RULE 22]: Import this dispatcher in production code that processes mixed-kind
 *   EffectComposer arrays. Avoids per-caller switch logic.
 *
 * @param {Object} row - EffectComposer row: { id, kind, ...kindFields }
 * @returns {Object|null} effectRegistry effect object, or null if not actionable
 */
export function effectComposerRowToEffect(row) {
  if (!row) return null;
  switch (row.kind) {
    case 'passiveBonus':
      return passiveBonusRowToEffect(row);
    case 'resistance':
      return resistanceRowToEffect(row);
    case 'vulnerability': // [PB-1 — 2026-06-13]
      return vulnerabilityRowToEffect(row);
    default:
      return null;
  }
}

/**
 * [SUB-PLAN 3A-followup-effectcomposer-resistance-kind — 2026-05-30]
 *
 * Convert an array of mixed-kind EffectComposer rows to effectRegistry effects.
 * Handles passiveBonus and resistance rows in the same array. Filters out null
 * results (blank / invalid rows).
 *
 * @param {Array} rows - Array of EffectComposer rows (any supported kind)
 * @returns {Array} Array of effectRegistry effect objects (may be empty)
 */
export function effectComposerRowsToEffects(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map(effectComposerRowToEffect).filter(Boolean);
}
